<template>
  <div
    class="carousel"
    @mousedown="onDragStart"
    @mousemove="onDragMove"
    @mouseup="onDragEnd"
    @mouseleave="onDragEnd"
    @touchstart="onDragStart"
    @touchmove="onDragMove"
    @touchend="onDragEnd"
  >
    <button class="carousel__nav carousel__nav--prev" @click="prevItem">
      <img
        :src="requireAsset('iconos/left.svg')"
        alt=""
        class="carousel__nav__icon"
      />
    </button>

    <div class="carousel__viewport">
      <TransitionGroup
        name="fade"
        tag="div"
        class="carousel__items"
        :style="carouselStyle"
      >
        <div
          v-for="(valor, valorIndex) in displayedValores"
          :key="valor.cod + '-' + valorIndex"
          class="carousel__item"
          :class="{
            active: valor.cod === activeCod,
            disabled: valoresDeshabilitados.includes(valor.cod)
          }"
          @click="selectItem(valor.cod)"
        >
          <div class="carousel__item__content">
            <img
              :src="
                requireAsset(
                  campo == 'mueble'
                    ? 'miniaturas/mueble/' + valor.cod + '.webp'
                    : valor.cod == 'personalizado'
                      ? 'miniaturas/personalizado.webp'
                      : 'miniaturas/' +
                        $route.params.producto +
                        '/' +
                        $route.params.modelo +
                        '/' +
                        valor.cod +
                        '.webp'
                )
              "
              alt=""
              class="carousel__item__miniatura"
            />
            <img
              v-if="valoresDeshabilitados.includes(valor.cod)"
              :src="requireAsset('iconos/stop.svg')"
              alt="No disponible"
              class="carousel__item__stop-icon"
            />

          </div>
          <span class="carousel__item__name">
            {{ valor.getLiteral($store.getters.getLanguage) }}
          </span>
        </div>
      </TransitionGroup>
    </div>

    <button class="carousel__nav carousel__nav--next" @click="nextItem">
      <img
        :src="requireAsset('iconos/right.svg')"
        alt=""
        class="carousel__nav__icon"
      />
    </button>
  </div>
</template>

<script>
export default {
  name: 'Carousel',
  props: {
    campo: String,
    valorSel: {
      type: String,
      default: '',
    },
    valores: {
      type: Array,
      default: () => [],
    },
    valoresDeshabilitados: {
      type: Array,
      default: () => [],
    },
    seleccion: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      activeCod: this.valores.some((v) => v.cod === this.valorSel)
        ? this.valorSel
        : this.valores.length > 0
          ? this.valores[0].cod
          : '',
      containerWidth: 0,
      itemWidth: 120,
      gap: 150,
      isDragging: false,
      dragStartX: 0,
      startSnapOffset: 0,
      dragOffset: 0,
      snapOffset: 0,
    };
  },
  computed: {
    displayedValores() {
      if (this.activeCod !== 'personalizado') {
        return this.valores.filter((valor) => valor.cod !== 'personalizado');
      }
      return this.valores;
    },
    activeIndex() {
      return this.displayedValores.findIndex(
        (valor) => valor.cod === this.activeCod
      );
    },
    carouselStyle() {
      const offset = this.isDragging ? this.dragOffset : this.snapOffset;
      return {
        transform: `translateX(${offset}px)`,
        transition: this.isDragging ? 'none' : 'transform 0.35s ease-in-out',
      };
    },
  },
  watch: {
    // If activeCod is filtered out or disabled, reset to 'sm'
    displayedValores(newList) {
      const isActiveInList = newList.some((v) => v.cod === this.activeCod);
      const isActiveDisabled = this.valoresDeshabilitados.includes(this.activeCod);

      // Si el valor activo no está en la lista O está deshabilitado
      if (!isActiveInList || isActiveDisabled) {
        // Obtener el mensaje apropiado según el tipo de mueble
        const message = this.getDisabledMessage(this.activeCod);

        this.$root.$emit('popup', this.getLiteral(message));
        this.activeCod = 'sm';
        this.$emit('valor', 'sm');
      }
    },
    // Watch para detectar cambios en valores deshabilitados
    valoresDeshabilitados: {
      handler(newDisabled) {
        // Si el valor activo se deshabilita, cambiar a 'sm'
        if (newDisabled.includes(this.activeCod)) {
          // Obtener el mensaje apropiado según el tipo de mueble
          const message = this.getDisabledMessage(this.activeCod);

          this.$root.$emit('popup', this.getLiteral(message));
          this.activeCod = 'sm';
          this.$emit('valor', 'sm');
        }
      },
      deep: true,
    },
    valorSel: {
      immediate: true,
      handler(newVal) {
        const found = this.valores.find((valor) => valor.cod === newVal);
        if (found) {
          this.activeCod = newVal;
        }
      },
    },
    activeCod() {
      this.updateSnapOffset();
    },
    valores: {
      handler() {
        this.$nextTick(() => {
          this.updateContainerWidth();
        });
      },
      deep: true,
    },
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateContainerWidth);
  },
  mounted() {
    this.updateContainerWidth();
    this.updateSnapOffset();
    window.addEventListener('resize', this.updateContainerWidth);

    // Si hay un activeCod (por defecto el primero) y es diferente al valorSel, emitirlo para seleccionarlo
    if (this.activeCod && this.activeCod !== this.valorSel) {
      this.$emit('valor', this.activeCod);
    }
  },
  methods: {
    getDisabledMessage(muebleType) {
      // Función helper para obtener el mensaje apropiado según el tipo de mueble
      let message = 'mueble.info.min-profundidad';

      if (!this.seleccion) return message;

      const dimz = this.seleccion.lavabo?.dimz || 0;
      const dimx = this.seleccion.lavabo?.dimx || 0;

      if (muebleType === 'unit') {
        message = 'mueble.info.unit';
      } else if (muebleType === 'cabinet') {
        const profundidadOK = dimz >= 0.4;
        const rangoAncho1OK = dimx < 0.471 || dimx > 0.750;
        const rangoAncho2OK = dimx < 1.001 || dimx > 1.125;

        if (!profundidadOK) {
          message = 'mueble.info.min-profundidad';
        } else if (!rangoAncho1OK) {
          message = 'mueble.info.cabinet1';
        } else if (!rangoAncho2OK) {
          message = 'mueble.info.cabinet2';
        }
      } else if (muebleType === 'line' || muebleType === 'drawer') {
        // Line y drawer tienen la misma condición de profundidad mínima
        if (dimz < 0.4) {
          message = 'mueble.info.min-profundidad';
        }
      }

      return message;
    },
    selectItem(cod) {
      // Si el valor está deshabilitado, no permitir seleccionarlo
      if (this.valoresDeshabilitados.includes(cod)) {
        return;
      }

      if (cod !== this.activeCod) {
        if (cod === 'personalizado' && this.activeCod !== 'personalizado') {
          return;
        }
        this.activeCod = cod;
        this.$emit('valor', cod);
      }
    },
    aplicarValor(cod) {
      this.$emit('valor', cod);
    },
    findNextEnabledIndex(startIndex, direction) {
      // Busca el siguiente índice habilitado en la dirección especificada
      // direction: 1 para adelante, -1 para atrás
      const totalItems = this.displayedValores.length;
      let currentIndex = startIndex;
      let attempts = 0;

      // Evitar bucle infinito: intentar máximo el número total de elementos
      while (attempts < totalItems) {
        // Calcular el siguiente índice con wrap-around
        currentIndex = (currentIndex + direction + totalItems) % totalItems;

        const itemCod = this.displayedValores[currentIndex].cod;

        // Si el elemento no está deshabilitado, retornarlo
        if (!this.valoresDeshabilitados.includes(itemCod)) {
          return currentIndex;
        }

        attempts++;
      }

      // Si todos están deshabilitados, retornar el índice actual
      return startIndex;
    },
    prevItem() {
      const currentIndex = this.activeIndex;
      const newIndex = this.findNextEnabledIndex(currentIndex, -1);
      this.selectItem(this.displayedValores[newIndex].cod);
    },
    nextItem() {
      const currentIndex = this.activeIndex;
      const newIndex = this.findNextEnabledIndex(currentIndex, 1);
      this.selectItem(this.displayedValores[newIndex].cod);
    },
    updateContainerWidth() {
      if (this.$el) {
        this.containerWidth = this.$el.clientWidth;
        this.updateSnapOffset();
      }
    },
    updateSnapOffset() {
      const totalItemWidth = this.itemWidth + this.gap;
      const offset =
        -(this.activeIndex * totalItemWidth) +
        (this.containerWidth / 2 - this.itemWidth / 2);
      this.snapOffset = offset;
    },
    onDragStart(e) {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      this.isDragging = true;
      this.dragStartX = clientX;
      this.startSnapOffset = this.snapOffset;
      this.dragOffset = this.snapOffset;
    },
    onDragMove(e) {
      if (!this.isDragging) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const diff = clientX - this.dragStartX;
      this.dragOffset = this.startSnapOffset + diff;
    },
    onDragEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;

      const totalItemWidth = this.itemWidth + this.gap;
      const distanceMoved = this.dragOffset - this.startSnapOffset;
      const itemsMoved = Math.round(distanceMoved / totalItemWidth) * -1;

      const totalItems = this.displayedValores.length;
      let targetIndex = (this.activeIndex + itemsMoved + totalItems) % totalItems;

      // Si el índice objetivo está deshabilitado, buscar el siguiente habilitado
      const targetCod = this.displayedValores[targetIndex].cod;
      if (this.valoresDeshabilitados.includes(targetCod)) {
        // Determinar dirección del movimiento para buscar en esa dirección
        const direction = itemsMoved > 0 ? 1 : itemsMoved < 0 ? -1 : 1;
        targetIndex = this.findNextEnabledIndex(targetIndex, direction);
      }

      this.selectItem(this.displayedValores[targetIndex].cod);
    },
  },
};
</script>

<style lang="scss" scoped>
.carousel {
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  overflow: hidden;
  user-select: none;
  &__nav {
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    padding: 0;
    background: rgba(0, 0, 0, 0.5);
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    width: 30px;
    height: 30px;
    transition: 0.35s ease-in-out opacity;
    &--prev {
      left: 10px;
    }
    &--next {
      right: 10px;
    }
  }
  &__viewport {
    display: flex;
    align-items: center;
    overflow: hidden;
    width: 100%;
    height: 200px;
  }
  &__items {
    display: flex;
    align-items: center;
    transition: 0.35s ease-in-out transform;
    gap: 150px;
  }
  &__item {
    transform: scale(0.8);
    flex: 0 0 auto;
    text-align: center;
    opacity: 0.5;
    transition:
      0.35s ease-in-out transform,
      0.35s ease-in-out opacity;
    transform-origin: center;
    width: 120px;
    overflow: visible;
    &__content {
      position: relative;
      width: 100%;
    }
    &__miniatura {
      width: 100%;
      height: auto;
      pointer-events: none;
    }
    &__stop-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 60%;
      height: auto;
      pointer-events: none;
      z-index: 2;
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
      opacity: 1 !important;
    }
    &__select-btn {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 10;
    }
    &__name {
      display: block;
      margin-top: 10px;
      font-size: 14px;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &:hover {
      opacity: 1;
    }
    &.active {
      opacity: 1;
      transform: scale(1.25);
      z-index: 1;
    }
    &.disabled {
      opacity: 0.3 !important;
      pointer-events: none;
      cursor: not-allowed;
      filter: grayscale(50%);
    }
  }
  &:active {
    cursor: grabbing;
  }
  @media screen and (max-height: 600px) and (max-width: 1050px) {
    &__nav {
      &--prev {
        left: 0;
        width: 20px;
      }
      &--next {
        right: 0;
        width: 20px;
      }
    }
    &__item {
      &__miniatura {
        width: 75%;
      }
    }
  }
}
</style>
